import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import React, { useCallback, useEffect } from 'react';

interface GalleryImage {
    src: string;
    alt: string;
}

interface ClassifiedGalleryProps {
    images: GalleryImage[];
    isOpen: boolean;
    currentIndex: number;
    onClose: () => void;
    onNavigate: (index: number) => void;
}

export const ClassifiedGallery: React.FC<ClassifiedGalleryProps> = ({ images, isOpen, currentIndex, onClose, onNavigate }) => {
    const handlePrevious = useCallback(() => {
        const newIndex = currentIndex > 0 ? currentIndex - 1 : images.length - 1;
        onNavigate(newIndex);
    }, [currentIndex, images.length, onNavigate]);

    const handleNext = useCallback(() => {
        const newIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0;
        onNavigate(newIndex);
    }, [currentIndex, images.length, onNavigate]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (!isOpen) return;

            switch (event.key) {
                case 'Escape':
                    onClose();
                    break;
                case 'ArrowLeft':
                    handlePrevious();
                    break;
                case 'ArrowRight':
                    handleNext();
                    break;
            }
        },
        [isOpen, onClose, handlePrevious, handleNext],
    );

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, handleKeyDown]);

    if (!isOpen || !images.length) return null;

    const currentImage = images[currentIndex];

    return (
        <div className="fixed inset-0 z-50 bg-black">
            {/* Header with back button */}
            <div className="absolute top-4 left-4 z-10">
                <Button onClick={onClose} className="rounded-lg bg-blue-800 px-6 py-2 font-medium hover:bg-blue-700">
                    Voltar
                </Button>
            </div>

            {/* Close button (X) */}
            <button
                onClick={onClose}
                className="absolute top-4 right-4 z-10 text-white transition-colors hover:text-gray-300"
                aria-label="Close gallery"
            >
                <X className="h-8 w-8" />
            </button>

            {/* Main image container */}
            <div className="flex h-full items-center justify-center px-4 pt-20 pb-32">
                <div className="relative h-full w-full max-w-5xl">
                    <img src={currentImage.src} alt={currentImage.alt} className="h-full w-full object-contain" />
                </div>
            </div>

            {/* Navigation arrows */}
            {images.length > 1 && (
                <>
                    <button
                        onClick={handlePrevious}
                        className="absolute top-1/2 left-4 -translate-y-1/2 p-2 text-white transition-colors hover:text-gray-300"
                        aria-label="Previous image"
                    >
                        <ChevronLeft className="h-8 w-8 sm:h-12 sm:w-12" />
                    </button>
                    <button
                        onClick={handleNext}
                        className="absolute top-1/2 right-4 -translate-y-1/2 p-2 text-white transition-colors hover:text-gray-300"
                        aria-label="Next image"
                    >
                        <ChevronRight className="h-8 w-8 sm:h-12 sm:w-12" />
                    </button>
                </>
            )}

            {/* Thumbnail navigation */}
            {images.length > 1 && (
                <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-2 rounded-lg bg-black/50 p-4 backdrop-blur-sm">
                    {images.slice(Math.max(0, currentIndex - 1), currentIndex + 3).map((image, index) => {
                        const actualIndex = Math.max(0, currentIndex - 1) + index;
                        const isActive = actualIndex === currentIndex;

                        return (
                            <button
                                key={actualIndex}
                                onClick={() => onNavigate(actualIndex)}
                                className={`relative h-16 w-16 overflow-hidden rounded-lg transition-all sm:h-20 sm:w-20 ${
                                    isActive ? 'scale-110 ring-2 ring-blue-500' : 'opacity-70 hover:opacity-100'
                                }`}
                                aria-label={`View image ${actualIndex + 1}`}
                            >
                                <img src={image.src} alt={image.alt} className="h-full w-full object-cover" />
                            </button>
                        );
                    })}

                    {/* Show more indicator if there are more images */}
                    {currentIndex + 3 < images.length && (
                        <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-black/70 text-sm text-white sm:h-20 sm:w-20">
                            +{images.length - (currentIndex + 3)}
                        </div>
                    )}
                </div>
            )}

            {/* Image counter */}
            <div className="absolute right-4 bottom-4 rounded-lg bg-black/50 px-3 py-1 text-white backdrop-blur-sm">
                {currentIndex + 1} / {images.length}
            </div>
        </div>
    );
};
